import React from "react";
import { Calendar } from "@/components/ui/calendar";
import { useI18n } from "@/i18n/I18nContext";
import { Loader2 } from "lucide-react";

const LABELS = {
  it: { title: "Calendario disponibilità", loading: "Caricamento...", blocked: "Non disponibile", available: "Disponibile", selected: "Selezionato" },
  en: { title: "Availability calendar", loading: "Loading...", blocked: "Unavailable", available: "Available", selected: "Selected" },
  fr: { title: "Calendrier de disponibilité", loading: "Chargement...", blocked: "Indisponible", available: "Disponible", selected: "Sélectionné" },
};

const dayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };

export const AvailabilityCalendar = ({ range, onRangeChange, blockedDays = new Set(), loading = false }) => {
  const { lang } = useI18n();
  const L = LABELS[lang] || LABELS.it;

  const isBlocked = (date) => blockedDays.has(dayKey(date));
  const isPast = (date) => { const t = new Date(); t.setHours(0, 0, 0, 0); return date < t; };
  // Nights of a stay are [from, to) — the check-out day itself is NOT a night.
  const nightsAllFree = (from, to) => {
    const cur = startOfDay(from), end = startOfDay(to);
    while (cur < end) { if (blockedDays.has(dayKey(cur))) return false; cur.setDate(cur.getDate() + 1); }
    return true;
  };
  // Turnover-aware selection: a "blocked" night can still be a valid CHECK-OUT day.
  const handleSelect = (next, clickedDay) => {
    if (!next || !next.from) { onRangeChange(undefined); return; }
    const from = startOfDay(next.from);
    const to = next.to ? startOfDay(next.to) : undefined;
    if (!to || +to === +from) {
      if (isPast(from) || isBlocked(from)) { onRangeChange(undefined); return; }
      onRangeChange({ from, to: undefined });
      return;
    }
    if (isPast(from) || isBlocked(from) || !nightsAllFree(from, to)) {
      const cd = clickedDay ? startOfDay(clickedDay) : null;
      if (cd && !isPast(cd) && !isBlocked(cd)) onRangeChange({ from: cd, to: undefined });
      else onRangeChange(undefined);
      return;
    }
    onRangeChange({ from, to });
  };

  return (
    <div data-testid="availability-calendar" className="bg-white rounded-2xl border border-black/10 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-xl text-[#1A1A1A]">{L.title}</h3>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-[#345B73]" />}
      </div>
      <Calendar
        mode="range"
        numberOfMonths={2}
        selected={range}
        onSelect={handleSelect}
        disabled={(d) => isPast(d)}
        modifiers={{ booked: (d) => isBlocked(d) }}
        modifiersClassNames={{ booked: "bg-[#7A2B33]/15 text-[#7A2B33] line-through" }}
        className="rounded-md"
      />
      <div className="mt-5 flex flex-wrap gap-4 text-xs text-[#5C5C5C]">
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[#7A2B33]/30" /> {L.blocked}</span>
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[#1D3557]" /> {L.selected}</span>
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full border border-[#1A1A1A]/20" /> {L.available}</span>
      </div>
    </div>
  );
};

export default AvailabilityCalendar;
